import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import WordDefinitionExercise from './WordDefinitionExercise';
import './WordDefinitionEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const WORD_DEFINITION_ICON = (
  <span className="default-icon-spacing" style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)'
  }}>
    <svg width="24" height="24" viewBox="0 0 512 512" fill="white">
      <g>
        <g>
          <path d="M260.995,227.715c-9.623,3.423-19.995-3.716-19.995-14.15V121h-71.455c-12.947,0-19.804,15.351-11.177,25.005
            C163.29,151.498,166,158.603,166,166c0,16.538-13.462,30-30,30c-16.538,0-30-13.462-30-30c0-7.397,2.71-14.502,7.632-19.995
            c8.627-9.654,1.77-25.005-11.177-25.005H45c-24.814,0-45,20.186-45,45v180c0,24.814,20.186,45,45,45h31c0,33.091,26.909,60,60,60
            s60-26.909,60-60h45v-92.563c0-10.296,10.712-17.369,19.995-14.15c18.633,6.636,39.99-8.335,40.005-28.286
            C301,236.021,279.657,221.064,260.995,227.715z"/>
        </g>
      </g>
      <g>
        <g>
          <path d="M467,121h-31c0-33.091-26.909-60-60-60s-60,26.909-60,60h-45v75c33.091,0,60,26.909,60,60s-26.909,60-60,60v75h196
            c24.814,0,45-20.186,45-45V166C512,141.186,491.814,121,467,121z"/>
        </g>
      </g>
    </svg>
  </span>
);

const WordDefinitionEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;
  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { myState, otherState, updateState, liveUpdateCount } = useExerciseSync(exerciseId);
  // Force ProseMirror to re-measure after expand/collapse so click coordinates stay accurate
  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror a REMOTE peer's expand/collapse live, without replaying stale
  // localStorage-hydrated data on every remount — see ExerciseSyncContext's
  // liveUpdateCount for why this can't just watch otherState.expanded alone.
  const lastLiveUpdateCountRef = React.useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const { pairs, title, level } = node.attrs;

  const isStudent = (() => {
    try {
      // Check if we're in student mode by looking for student-specific attributes
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      
      // Check if we're in a student dashboard context
      const isStudentDashboard = typeof document !== 'undefined' && 
        (window.location.pathname.includes('/student-dashboard') || 
         window.location.pathname.includes('/student-profile'));
      
      return isStudentDashboard;
    } catch (_) { return false; }
  })();

  // Allow placing cursor above/below the embed (like ABCD/OpenQuestions)
  const insertParagraphAt = (pos) => {
    if (!editor || typeof pos !== 'number') return;
    editor
      .chain()
      .focus()
      .insertContentAt(pos, { type: 'paragraph' })
      .setTextSelection(pos + 1)
      .run();
  };

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = typeof getPos === 'function' ? getPos() : null;
    insertParagraphAt(pos);
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const base = typeof getPos === 'function' ? getPos() : null;
    if (typeof base === 'number' && node && typeof node.nodeSize === 'number') {
      insertParagraphAt(base + node.nodeSize);
    }
  };

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    updateState({ expanded: next });
  };

  const handleClose = () => {
    setIsExpanded(false);
    updateState({ expanded: false });
  };

  if (!pairs || !Array.isArray(pairs)) {
    return (
      <NodeViewWrapper>
        <div className="word-definition-embed-error">
          <p>Error: Invalid Word Definition exercise data</p>
        </div>
      </NodeViewWrapper>
    );
  }

  const isHomework = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-tab-type');
      if (fromRoot) return fromRoot === 'homework';
      const fromBody = typeof document !== 'undefined' ? document.body.getAttribute('data-novas-current-tab-type') : null;
      return fromBody === 'homework';
    } catch (_) { return false; }
  })();

  // Drag handlers for visual feedback
  const handleDragStart = (e) => {
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
    e.target.style.outline = 'none';
    e.target.style.border = 'none';
    e.target.style.boxShadow = 'none';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.style.transform = '';
    e.target.style.outline = '';
    e.target.style.border = '';
    e.target.style.boxShadow = '';
    e.target.classList.remove('dragging');
  };

  // Only mark the outer wrapper / preview as a drag handle when the embed
  // is collapsed. With the expanded view also flagged as data-drag-handle,
  // ProseMirror treats clicks inside it (e.g. the Close button) as
  // node-selection / drag-start gestures — the browser briefly renders its
  // native drag-ghost snapshot of the whole card before the drag is
  // cancelled, which is what shows up as a black flash on close. Same fix
  // as WordTranslation and ABCD embeds.
  const dragProps = isExpanded
    ? {}
    : { draggable: true, 'data-drag-handle': '', onDragStart: handleDragStart, onDragEnd: handleDragEnd };

  return (
    <NodeViewWrapper className="word-definition-embed" contentEditable={false} {...dragProps}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div className="word-definition-exercise-embed" {...dragProps}>
        {!isExpanded ? (
          <div className="embed-preview unified-exercise-preview" onClick={handleToggle} {...dragProps}>
            {WORD_DEFINITION_ICON}
            <div className="unified-preview-main">
              <div className="unified-preview-title">{title || 'Word Definition Matching'}</div>
              <div className="unified-preview-meta">
                {pairs.length} pairs · Level: {level || 'B1'}
              </div>
            </div>
            <div className="unified-preview-actions" onClick={e => e.stopPropagation()}>
              <button
                className="unified-icon-btn"
                title="Add to homework"
                style={{ display: (isHomework || isStudent) ? 'none' : 'inline-flex' }}
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('novas:add-to-homework', {
                      detail: {
                        type: 'wordDefinition',
                        attrs: { pairs, title, level }
                      }
                    }));
                  } catch (_) {}
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h4a.5.5 0 1 0 0-1h-4a.5.5 0 0 1-.5-.5V7.207l5-5 6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z"/>
                  <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1h-1a.5.5 0 0 0 0 1h1v1a.5.5 0 1 0 1 0v-1h1a.5.5 0 1 0 0-1h-1v-1a.5.5 0 0 0-.5-.5"/>
                </svg>
              </button>
              <button
                className="unified-icon-btn"
                title="Delete"
                style={{ display: isStudent ? 'none' : 'inline-flex' }}
                onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="wde-expanded">
            <WordDefinitionExercise
              pairs={pairs}
              title={title}
              level={level}
              onClose={handleClose}
              updateAttributes={updateAttributes}
              node={node}
              editor={editor}
              getPos={getPos}
              isStudent={isStudent}
              myState={myState}
              otherState={otherState}
              updateState={updateState}
            />
          </div>
        )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default WordDefinitionEmbed; 